import type { UserWorkspace } from '@/api/types'

/**
 * Unread-count arithmetic for notifications.
 *
 * The per-workspace count is not a store of its own here. The server already
 * puts it on each workspace as `unread_notifications_count`, so the workspace
 * list *is* the count, and the badge reads it straight from that cache. The Vue
 * app keeps a parallel `perWorkspaceUnreadCount` map built from exactly those
 * fields, which then has to be rebuilt whenever the workspaces are refetched.
 */

/** One workspace's share of a batch of new notifications. */
export interface UnreadDelta {
  /** `null` for a notification addressed to the user rather than a workspace. */
  workspace_id: number | null
  count: number
}

export function changeWorkspaceUnread(
  workspaces: UserWorkspace[],
  workspaceId: number,
  delta: number,
): UserWorkspace[] {
  const index = workspaces.findIndex((workspace) => workspace.id === workspaceId)
  if (index === -1) return workspaces

  const workspace = workspaces[index]!
  // Clamped, as upstream does. The counts are patched from events rather than
  // recounted, so a mark-as-read arriving for a notification this client never
  // saw would otherwise drive the badge negative and leave it there.
  const count = Math.max(0, workspace.unread_notifications_count + delta)
  if (count === workspace.unread_notifications_count) return workspaces

  const next = [...workspaces]
  next[index] = { ...workspace, unread_notifications_count: count }
  return next
}

export function applyUnreadDeltas(
  workspaces: UserWorkspace[],
  deltas: UnreadDelta[],
): UserWorkspace[] {
  return deltas.reduce(
    (current, delta) =>
      delta.workspace_id === null
        ? current
        : changeWorkspaceUnread(current, delta.workspace_id, delta.count),
    workspaces,
  )
}

/** A notification, as much of one as the count arithmetic needs. */
export interface IncomingNotification {
  workspace: { id: number } | null
  read?: boolean
}

/**
 * Groups a batch of new notifications into per-workspace unread deltas.
 *
 * Only the unread ones count. A notification can arrive already read -- the
 * sender's own copy of something they triggered -- and counting it would leave
 * a badge that never clears.
 */
export function unreadDeltasFor(notifications: IncomingNotification[]): UnreadDelta[] {
  const counts = new Map<number | null, number>()
  for (const notification of notifications) {
    if (notification.read === true) continue
    const workspaceId = notification.workspace?.id ?? null
    counts.set(workspaceId, (counts.get(workspaceId) ?? 0) + 1)
  }
  return [...counts].map(([workspace_id, count]) => ({ workspace_id, count }))
}

/** The share of a batch addressed to the user rather than to a workspace. */
export function userUnreadDelta(deltas: UnreadDelta[]): number {
  return deltas
    .filter((delta) => delta.workspace_id === null)
    .reduce((total, delta) => total + delta.count, 0)
}
